import { lazy } from "react";

// ─── Lazy views ──────────────────────────────────────────────────────────────

const AdminDashboard      = lazy(() => import("../views/admin/AdminDashboard"));
const AdminAttendance     = lazy(() => import("../views/admin/AdminAttendance"));
const AdminFeeManager     = lazy(() => import("../views/admin/AdminFeeManager"));
const AdminFeeStructures  = lazy(() => import("../views/admin/AdminFeeStructures"));
const AdminTeacherLogs    = lazy(() => import("../views/admin/AdminTeacherLogs"));
const AdminTimetable      = lazy(() => import("../views/admin/AdminTimetable"));
const AdminReports        = lazy(() => import("../views/admin/AdminReports"));
const AdminSchoolTimings  = lazy(() => import("../views/admin/AdminSchoolTimings"));
const SuperAdminDashboard = lazy(() => import("../views/admin/SuperAdminDashboard"));
const AdminCoachingCenters = lazy(() => import("../views/admin/AdminCoachingCenters"));
const AdminUsers          = lazy(() => import("../views/admin/AdminUsers"));
const AdminAnnouncements  = lazy(() => import("../views/admin/AdminAnnouncements"));

const StudentDirectory    = lazy(() => import("./features/students/StudentDirectory"));
const StaffDirectoryOptimized      = lazy(() => import("./features/staff/StaffDirectoryOptimized"));

const TeacherDashboard    = lazy(() => import("../views/teacher/TeacherDashboard"));
const TeacherSchedule     = lazy(() => import("../views/teacher/TeacherSchedule"));
const TeacherDailyLog     = lazy(() => import("../views/teacher/TeacherDailyLog"));
const TeacherGradebook    = lazy(() => import("../views/teacher/TeacherGradebook"));
const TeacherExams        = lazy(() => import("../views/teacher/TeacherExams"));
const TeacherPaperMaker   = lazy(() => import("../views/teacher/TeacherPaperMaker"));
const TeacherAssignments  = lazy(() => import("../views/teacher/TeacherAssignments"));
const TeacherHomework     = lazy(() => import("../views/teacher/TeacherHomework"));
const TeacherTestBuilder  = lazy(() => import("../views/teacher/TeacherTestBuilder"));
const TeacherAnnouncements = lazy(() => import("../views/teacher/TeacherAnnouncements"));
const TeacherMaterials    = lazy(() => import("../views/teacher/TeacherMaterials"));
const TeacherLeave        = lazy(() => import("../views/teacher/TeacherLeave"));
const TeacherReports      = lazy(() => import("../views/teacher/TeacherReports"));
const TeacherFees         = lazy(() => import("../views/teacher/TeacherFees"));
const ClassFeedbackPage   = lazy(() => import("../views/teacher/ClassFeedbackPage"));

const StudentDashboard    = lazy(() => import("../views/student/StudentDashboard"));
const StudentAnnouncements = lazy(() => import("../views/student/StudentAnnouncements"));
const StudentFees         = lazy(() => import("../views/student/StudentFees"));
const StudentMaterials    = lazy(() => import("../views/student/StudentMaterials"));
const StudentLeaderboard  = lazy(() => import("../views/student/StudentLeaderboard"));
const StudentReportCard   = lazy(() => import("../views/student/StudentReportCard"));
const StudentCalendar     = lazy(() => import("../views/student/StudentCalendar"));

const TutorDashboard      = lazy(() => import("../views/tutor/TutorDashboard"));
const IndividualDashboard = lazy(() => import("../views/individual/IndividualDashboard"));
const ChatPage            = lazy(() => import("../views/chat/ChatPage"));
const AdminParents        = lazy(() => import("../views/admin/AdminParents"));

// ─── Placeholder ─────────────────────────────────────────────────────────────

export function PlaceholderPage({ label }) {
  return (
    <div className="flex items-center justify-center h-[400px] text-lg text-navy-400 font-sora">
      <div className="text-center">
        <div className="text-5xl mb-4 opacity-30">🚧</div>
        <div>{label}</div>
      </div>
    </div>
  );
}

// ─── Admin ───────────────────────────────────────────────────────────────────

export function AdminRoutes({ nav, t, setNav, stuData, setStuData, teachData, setTeachData, teacherLogs, setTeacherLogs, timetable, setTimetable, tests, fetchAllData, lang, user }) {
  switch (nav) {
    case "dashboard":    return <AdminDashboard t={t} setNav={setNav} />;
    case "attendance":   return <AdminAttendance t={t} data={stuData} teachData={teachData} tests={tests} timetable={timetable} />;
    case "students":     return <StudentDirectory t={t} lang={lang} isAdmin user={user} onStudentChange={fetchAllData} />;
    case "staff":        return <StaffDirectoryOptimized t={t} lang={lang} user={user} data={teachData} setData={setTeachData} studentData={stuData} />;
    case "timetable":    return <AdminTimetable t={t} timetable={timetable} setTimetable={setTimetable} teachData={teachData} />;
    case "fees":         return <AdminFeeManager t={t} data={stuData} setData={setStuData} />;
    case "feestructures":return <AdminFeeStructures />;
    case "teacherlogs":  return <AdminTeacherLogs t={t} teacherLogs={teacherLogs} setTeacherLogs={setTeacherLogs} teachData={teachData} />;
    case "reports":      return <AdminReports t={t} stuData={stuData} teachData={teachData} timetable={timetable} />;
    case "schooltimings":return <AdminSchoolTimings t={t} />;
    case "superadmin":   return <SuperAdminDashboard t={t} />;
    // case "coachingcenters": return <AdminCoachingCenters t={t} />;
    case "adminusers":        return <AdminUsers t={t} />;
    case "announcements":     return <AdminAnnouncements user={user} teachData={teachData} />;
    case "parents":           return <AdminParents />;
    default:                  return <PlaceholderPage label="Feature coming soon" />;
  }
}

// ─── Teacher ─────────────────────────────────────────────────────────────────

export function TeacherRoutes({ nav, t, lang, user, stuData, setStuData, teachData, setTeachData, curriculum, setCurriculum, tests, setTests, homework, setHomework, teacherLogs, setTeacherLogs, timetable, onCheckIn, setNav, fetchAllData }) {
  switch (nav) {
    case "dashboard":
      return (
        <TeacherDashboard
          t={t} lang={lang} data={stuData} setData={setStuData} user={user}
          staffData={teachData} onCheckIn={onCheckIn} curriculum={curriculum}
          setCurriculum={setCurriculum} tests={tests} setTests={setTests}
          homework={homework} setHomework={setHomework} teacherLogs={teacherLogs}
          setTeacherLogs={setTeacherLogs} timetable={timetable} setNav={setNav}
        />
      );
    case "myschedule":   return <TeacherSchedule t={t} user={user} timetable={timetable} staffData={teachData} />;
    case "dailylog":     return <TeacherDailyLog user={user} teacherLogs={teacherLogs} setTeacherLogs={setTeacherLogs} />;
    case "gradebook":    return <TeacherGradebook user={user} stuData={stuData} setStuData={setStuData} />;
    case "students":     return <StudentDirectory t={t} lang={lang} isAdmin={false} user={user} onStudentChange={fetchAllData} />;
    case "staff":        return <StaffDirectoryOptimized t={t} lang={lang} user={user} data={teachData} setData={setTeachData} studentData={stuData} />;
    case "exams":        return <TeacherExams user={user} tests={tests} setTests={setTests} setNav={setNav} />;
    case "papermaker":   return <TeacherPaperMaker user={user} />;
    case "testbuilder":  return <TeacherTestBuilder user={user} />;
    case "homework":     return <TeacherHomework user={user} />;
    case "assignments":  return <TeacherAssignments user={user} stuData={stuData} homework={homework} setHomework={setHomework} />;
    case "announcements":return <TeacherAnnouncements user={user} teachData={teachData} />;
    case "materials":    return <TeacherMaterials user={user} />;
    case "leave":        return <TeacherLeave user={user} />;
    case "reports":      return <TeacherReports user={user} stuData={stuData} />;
    case "fees":          return <TeacherFees user={user} stuData={stuData} />;
    case "chat":          return <ChatPage user={user} onBack={() => setNav('dashboard')} />;
    case "classfeedback": return <ClassFeedbackPage user={user} />;
    default:              return <PlaceholderPage label="Feature coming soon" />;
  }
}

// ─── Student ─────────────────────────────────────────────────────────────────

export function StudentRoutes({ nav, setNav, user, stuData, tests, curriculum, homework, timetable, assignments }) {
  switch (nav) {
    case "announcements": return <StudentAnnouncements user={user} />;
    case "chat":          return <ChatPage user={user} onBack={() => setNav('dashboard')} />;
    case "fees":          return <StudentFees user={user} stuData={stuData} />;
    case "materials":     return <StudentMaterials user={user} />;
    case "leaderboard":   return <StudentLeaderboard user={user} stuData={stuData} />;
    case "reportcard":    return <StudentReportCard user={user} stuData={stuData} assignments={assignments} />;
    case "calendar":      return <StudentCalendar user={user} tests={tests} homework={homework} />;
    case "dashboard":
    default:
      return <StudentDashboard user={user} tests={tests} curriculum={curriculum} stuData={stuData} homework={homework} timetable={timetable} />;
  }
}

// ─── Tutor ───────────────────────────────────────────────────────────────────

export function TutorRoutes({ nav, user, students, setStudents, homework, setHomework }) {
  return (
    <TutorDashboard
      nav={nav} user={user}
      students={students} setStudents={setStudents}
      homework={homework} setHomework={setHomework}
    />
  );
}

// ─── Individual ──────────────────────────────────────────────────────────────

export function IndividualRoutes({ nav, user }) {
  return <IndividualDashboard nav={nav} user={user} />;
}